import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function SummaryAnalytics({
    difficultyStats,
    skillsAnalysis,
    selectedLevel,
    onSelectLevel,
    selectedSkill,
    onSelectSkill,
    hideLevel = false
}) {
    const { t } = useTranslation('common');
    const [openGroups, setOpenGroups] = useState({
        dimensions: true,
        other: true
    });

    const [openDomains, setOpenDomains] = useState({});
    const [openComps, setOpenComps] = useState({});
    const [skillStatusFilter, setSkillStatusFilter] = useState('all'); // 'all' | 'strong' | 'weak'

    const toggleGroup = (key) => {
        setOpenGroups(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const toggleDomain = (key) => {
        setOpenDomains(prev => ({
            ...prev,
            [key]: prev[key] === undefined ? false : !prev[key] // default open
        }));
    };

    const toggleComp = (key) => {
        setOpenComps(prev => ({
            ...prev,
            [key]: prev[key] === undefined ? false : !prev[key] // default open
        }));
    };

    const { easy, medium, hard } = difficultyStats || {};
    const {
        domainTree = [],
        otherSkillsTree = [],
        hasSkills = false
    } = skillsAnalysis || {};

    const levels = [
        { ...easy, key: 'easy', icon: 'fa-feather-alt', color: '#16a34a' },
        { ...medium, key: 'medium', icon: 'fa-tachometer-alt', color: '#f59e0b' },
        { ...hard, key: 'hard', icon: 'fa-fire-alt', color: '#ef4444' }
    ].filter(l => l && l.total > 0);

    const getColorForPct = (p) => {
        if (p >= 75) return '#16a34a';
        if (p >= 60) return '#d97706';
        return '#dc2626';
    };

    const getStatusInfo = (skill) => {
        const pct = skill.masteryPct ?? 0;
        if (pct >= 75) {
            return {
                key: 'strong',
                label: 'متقنة',
                badgeClass: 'badge-success',
                icon: 'fa-check-circle',
                color: '#16a34a',
                bgColor: '#f0fdf4'
            };
        }
        if (pct >= 60) {
            return {
                key: 'moderate',
                label: 'متوسطة',
                badgeClass: 'badge-warning',
                icon: 'fa-minus-circle',
                color: '#d97706',
                bgColor: '#fffbeb'
            };
        }
        return {
            key: 'weak',
            label: 'بحاجة لتحسين',
            badgeClass: 'badge-danger',
            icon: 'fa-exclamation-circle',
            color: '#dc2626',
            bgColor: '#fef2f2'
        };
    };

    const handleFilterItem = (type, item) => {
        if (!onSelectSkill) return;
        const currentName = selectedSkill?.name;
        if (currentName === item.name) {
            onSelectSkill(null); // Toggle off
        } else {
            onSelectSkill({
                type,
                id: item.id || null,
                name: item.name,
                questionIds: item.questionIds || item.associatedQuestions || []
            });
        }
    };

    const handleLevelClick = (key) => {
        if (!onSelectLevel) return;
        if (selectedLevel === key) {
            onSelectLevel(null); // Toggle off
        } else {
            onSelectLevel(key);
        }
    };

    // Filter skills by status tab if selected
    const filterSkillByTab = (skill) => {
        if (skillStatusFilter === 'all') return true;
        if (skillStatusFilter === 'strong') return (skill.masteryPct ?? 0) >= 75;
        if (skillStatusFilter === 'weak') return (skill.masteryPct ?? 0) < 60;
        return true;
    };

    return (
        <div className="d-flex flex-column gap-3" style={{ gap: '0.85rem' }}>
            {/* 1. Difficulty Level Breakdown (Clickable to Filter) */}
            {!hideLevel && (
                <div className="analytics-section-box anim-fade-up">
                    <div className="analytics-section-title d-flex justify-content-between align-items-center">
                        <span className="d-flex align-items-center font-weight-bold" style={{ gap: '0.45rem' }}>
                            <i className="fas fa-layer-group text-primary"></i>
                            <span>{t('Pages.Lessons.Summary.difficultyStats') || 'إحصائيات مستوى الصعوبة'}</span>
                        </span>
                        <span className="text-muted small" style={{ fontSize: '0.72rem' }}>
                            (انقر للتصفية)
                        </span>
                    </div>

                    <div className="d-flex flex-column gap-2" style={{ gap: '0.55rem' }}>
                        {levels.map(level => {
                            const isLevelActive = selectedLevel === level.key;
                            return (
                                <div
                                    key={level.key}
                                    className={`diff-stat-item ${level.key} cursor-pointer ${isLevelActive ? 'shadow-sm' : ''}`}
                                    onClick={() => handleLevelClick(level.key)}
                                    style={{
                                        cursor: 'pointer',
                                        transition: 'all 0.15s ease',
                                        outline: isLevelActive ? `2px solid ${level.color}` : 'none',
                                        borderRadius: '0.65rem'
                                    }}
                                    title={`تصفية حسب الأسئلة ذات المستوى: ${level.label}`}
                                >
                                    <div className="d-flex align-items-center justify-content-between">
                                        <span className="d-flex align-items-center font-weight-bold" style={{ gap: '0.35rem', fontSize: '0.82rem' }}>
                                            <i className={`fas ${level.icon}`}></i>
                                            <span>{level.label}</span>
                                            {isLevelActive && <span className="badge badge-primary mr-1" style={{ fontSize: '0.68rem' }}>محدد</span>}
                                        </span>
                                        <span className="font-weight-bold" style={{ fontSize: '0.85rem', color: level.color }}>
                                            {level.accuracy}%
                                        </span>
                                    </div>

                                    <div className="kpi-bar" style={{ height: '5px' }}>
                                        <div className="fill" style={{ width: `${level.accuracy}%`, background: level.color }} />
                                    </div>

                                    <div className="d-flex justify-content-between text-muted" style={{ fontSize: '0.72rem' }}>
                                        <span>{level.correct} من {level.total} أسئلة صحيحة</span>
                                        <span>{Number(level.userMarks).toFixed(1)} / {level.maxMarks} علامة</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* 2. Unified 3-Level Skills Hierarchy & Evaluation (Directly in Tree) */}
            {hasSkills && (
                <div className="analytics-section-box anim-fade-up">
                    <div className="analytics-section-title d-flex justify-content-between align-items-center mb-2">
                        <span className="d-flex align-items-center font-weight-bold" style={{ gap: '0.45rem' }}>
                            <i className="fas fa-award text-warning"></i>
                            <span>{t('Pages.Lessons.Summary.skillsEvaluation') || 'تقييم المهارات التعليمية'}</span>
                        </span>
                        <span className="text-muted small" style={{ fontSize: '0.72rem' }}>
                            (انقر لتصفية الأسئلة)
                        </span>
                    </div>

                    {/* Quick Status Filter Tabs */}
                    <div className="d-flex align-items-center justify-content-start mb-3" style={{ gap: '0.35rem' }}>
                        <button
                            type="button"
                            className={`btn btn-sm py-1 px-2 ${skillStatusFilter === 'all' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            style={{ fontSize: '0.72rem', borderRadius: '0.4rem' }}
                            onClick={() => setSkillStatusFilter('all')}
                        >
                            كافة المهارات
                        </button>
                        <button
                            type="button"
                            className={`btn btn-sm py-1 px-2 ${skillStatusFilter === 'strong' ? 'btn-success text-white' : 'btn-outline-success'}`}
                            style={{ fontSize: '0.72rem', borderRadius: '0.4rem' }}
                            onClick={() => setSkillStatusFilter('strong')}
                        >
                            <i className="fas fa-check-circle ml-1"></i>
                            المتقنة (≥ 75%)
                        </button>
                        <button
                            type="button"
                            className={`btn btn-sm py-1 px-2 ${skillStatusFilter === 'weak' ? 'btn-danger text-white' : 'btn-outline-danger'}`}
                            style={{ fontSize: '0.72rem', borderRadius: '0.4rem' }}
                            onClick={() => setSkillStatusFilter('weak')}
                        >
                            <i className="fas fa-exclamation-circle ml-1"></i>
                            بحاجة لتحسين (&lt; 60%)
                        </button>
                    </div>

                    {/* 3-Level Grouped Skills Tree with Points & Results at EACH Level */}
                    <div>
                        {/* A. Curriculum Dimensions & Components Tree */}
                        {domainTree.length > 0 && (
                            <div className="skills-group-node mb-2">
                                <div className="skills-group-title" onClick={() => toggleGroup('dimensions')}>
                                    <span className="text-primary font-weight-bold d-flex align-items-center">
                                        <i className="fas fa-lightbulb ml-1 text-warning"></i>
                                        <span>{t('Skills') || 'المهارات والمجالات التعليمية'}</span>
                                    </span>
                                    <i className={`fas fa-chevron-${openGroups.dimensions ? 'up' : 'down'} text-muted`}></i>
                                </div>

                                {openGroups.dimensions && (
                                    <div className="pr-1 my-2">
                                        {domainTree.map((domain, dIdx) => {
                                            const isDomainOpen = openDomains[`d_${dIdx}`] !== false;
                                            const isDomainActive = selectedSkill?.name === domain.name;

                                            // Filter components by active skillStatusFilter
                                            const filteredComponents = domain.components
                                                .map(comp => ({
                                                    ...comp,
                                                    skills: comp.skills.filter(filterSkillByTab)
                                                }))
                                                .filter(comp => comp.skills.length > 0);

                                            if (skillStatusFilter !== 'all' && filteredComponents.length === 0) {
                                                return null;
                                            }

                                            return (
                                                <div key={`domain-${dIdx}`} className="mb-3 border rounded p-2 bg-white shadow-sm">
                                                    {/* Level 1: Domain Header with Points & Result */}
                                                    <div className="d-flex align-items-center justify-content-between pb-1 border-bottom">
                                                        <div
                                                            className={`d-flex align-items-center cursor-pointer font-weight-bold ${isDomainActive ? 'text-primary' : 'text-dark'}`}
                                                            style={{ fontSize: '0.82rem', gap: '0.35rem' }}
                                                            onClick={() => handleFilterItem('domain', domain)}
                                                            title={`تصفية كافة أسئلة المجال: ${domain.name} (${domain.totalQuestions} أسئلة)`}
                                                        >
                                                            <i className="fas fa-folder text-warning"></i>
                                                            <span>{domain.name}</span>
                                                            <span className="badge badge-light text-muted" style={{ fontSize: '0.7rem' }}>
                                                                {domain.totalQuestions} أسئلة
                                                            </span>
                                                        </div>

                                                        <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                                            <div className="d-flex flex-column align-items-end" style={{ fontSize: '0.74rem' }}>
                                                                <span className="font-weight-bold" style={{ color: getColorForPct(domain.masteryPct) }}>
                                                                    {Number(domain.userMarks).toFixed(1)} / {domain.maxMarks} علامة ({domain.masteryPct}%)
                                                                </span>
                                                            </div>
                                                            <i
                                                                className={`fas fa-chevron-${isDomainOpen ? 'up' : 'down'} text-muted cursor-pointer px-1`}
                                                                onClick={() => toggleDomain(`d_${dIdx}`)}
                                                            ></i>
                                                        </div>
                                                    </div>

                                                    {/* Level 2 & 3: Components and Skills */}
                                                    {isDomainOpen && (
                                                        <div className="pr-2 pt-2" style={{ borderRight: '2px solid #e2e8f0', marginRight: '4px' }}>
                                                            {filteredComponents.map((comp, cIdx) => {
                                                                const isCompOpen = openComps[`c_${dIdx}_${cIdx}`] !== false;
                                                                const isCompActive = selectedSkill?.name === comp.name;

                                                                return (
                                                                    <div key={`comp-${cIdx}`} className="mb-2">
                                                                        {/* Level 2: Component Header with Points & Result */}
                                                                        <div className="d-flex align-items-center justify-content-between p-1 bg-light rounded">
                                                                            <div
                                                                                className={`d-flex align-items-center cursor-pointer font-weight-bold ${isCompActive ? 'text-info' : 'text-secondary'}`}
                                                                                style={{ fontSize: '0.76rem', gap: '0.3rem' }}
                                                                                onClick={() => handleFilterItem('component', comp)}
                                                                                title={`تصفية أسئلة البعد: ${comp.name} (${comp.totalQuestions} أسئلة)`}
                                                                            >
                                                                                <i className="fas fa-folder-open text-info"></i>
                                                                                <span>{comp.name}</span>
                                                                                <span className="badge badge-light text-muted" style={{ fontSize: '0.68rem' }}>
                                                                                    {comp.totalQuestions} أسئلة
                                                                                </span>
                                                                            </div>

                                                                            <div className="d-flex align-items-center" style={{ gap: '0.4rem' }}>
                                                                                <span className="font-weight-bold" style={{ fontSize: '0.72rem', color: getColorForPct(comp.masteryPct) }}>
                                                                                    {Number(comp.userMarks).toFixed(1)} / {comp.maxMarks} ({comp.masteryPct}%)
                                                                                </span>
                                                                                <i
                                                                                    className={`fas fa-chevron-${isCompOpen ? 'up' : 'down'} text-muted cursor-pointer`}
                                                                                    style={{ fontSize: '0.7rem' }}
                                                                                    onClick={() => toggleComp(`c_${dIdx}_${cIdx}`)}
                                                                                ></i>
                                                                            </div>
                                                                        </div>

                                                                        {/* Level 3: Individual Skills with Status Badges */}
                                                                        {isCompOpen && (
                                                                            <div className="pr-2 pt-1" style={{ borderRight: '2px solid #cbd5e1', marginRight: '4px' }}>
                                                                                {comp.skills.map((skill, sIdx) => {
                                                                                    const isSkillActive = selectedSkill?.name === skill.name;
                                                                                    const status = getStatusInfo(skill);

                                                                                    return (
                                                                                        <div
                                                                                            key={`skill-${sIdx}`}
                                                                                            className={`skill-leaf-item cursor-pointer ${isSkillActive ? 'bg-indigo-50 font-weight-bold' : ''}`}
                                                                                            onClick={() => handleFilterItem('skill', skill)}
                                                                                            style={{ cursor: 'pointer' }}
                                                                                            title={`انقر لتصفية الأسئلة (${skill.totalQuestions} أسئلة - ${Number(skill.userMarks).toFixed(1)}/${skill.maxMarks} علامة)`}
                                                                                        >
                                                                                            <div className="d-flex align-items-center" style={{ gap: '0.35rem' }}>
                                                                                                <i className={`fas ${status.icon}`} style={{ color: status.color, fontSize: '0.75rem' }}></i>
                                                                                                <span className="text-dark" style={{ fontSize: '0.75rem' }}>
                                                                                                    {skill.name}
                                                                                                </span>
                                                                                            </div>

                                                                                            <div className="d-flex align-items-center" style={{ gap: '0.4rem' }}>
                                                                                                <span className="text-muted" style={{ fontSize: '0.7rem' }}>
                                                                                                    {Number(skill.userMarks).toFixed(1)}/{skill.maxMarks}
                                                                                                </span>
                                                                                                <div className="skill-leaf-bar">
                                                                                                    <div className="skill-leaf-bar-fill" style={{ width: `${skill.masteryPct}%`, background: status.color }} />
                                                                                                </div>
                                                                                                <span className="font-weight-bold" style={{ color: status.color, minWidth: '28px' }}>
                                                                                                    {skill.masteryPct}%
                                                                                                </span>
                                                                                            </div>
                                                                                        </div>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* B. Other Skills Tree */}
                        {otherSkillsTree.length > 0 && (
                            <div className="skills-group-node">
                                <div className="skills-group-title" onClick={() => toggleGroup('other')}>
                                    <span className="text-secondary font-weight-bold d-flex align-items-center">
                                        <i className="fas fa-layer-group ml-1"></i>
                                        <span>{t('OtherSkills') || 'مهارات أخرى'}</span>
                                    </span>
                                    <i className={`fas fa-chevron-${openGroups.other ? 'up' : 'down'} text-muted`}></i>
                                </div>

                                {openGroups.other && (
                                    <div className="pr-1 my-2">
                                        {otherSkillsTree.map((domain, oIdx) => {
                                            const isOtherDomainActive = selectedSkill?.name === domain.name;

                                            const filteredSubDomains = domain.subDomains
                                                .map(sub => ({
                                                    ...sub,
                                                    skills: sub.skills.filter(filterSkillByTab)
                                                }))
                                                .filter(sub => sub.skills.length > 0);

                                            if (skillStatusFilter !== 'all' && filteredSubDomains.length === 0) {
                                                return null;
                                            }

                                            return (
                                                <div key={`other-domain-${oIdx}`} className="mb-3 border rounded p-2 bg-white shadow-sm">
                                                    {/* Level 1 Header */}
                                                    <div className="d-flex align-items-center justify-content-between pb-1 border-bottom">
                                                        <div
                                                            className={`d-flex align-items-center cursor-pointer font-weight-bold ${isOtherDomainActive ? 'text-primary' : 'text-dark'}`}
                                                            style={{ fontSize: '0.82rem', gap: '0.35rem' }}
                                                            onClick={() => handleFilterItem('domain', domain)}
                                                        >
                                                            <i className="fas fa-folder text-warning"></i>
                                                            <span>{domain.name}</span>
                                                        </div>
                                                        <span className="font-weight-bold" style={{ fontSize: '0.74rem', color: getColorForPct(domain.masteryPct) }}>
                                                            {Number(domain.userMarks).toFixed(1)} / {domain.maxMarks} علامة ({domain.masteryPct}%)
                                                        </span>
                                                    </div>

                                                    {/* Level 2 & 3 */}
                                                    <div className="pr-2 pt-2" style={{ borderRight: '2px solid #e2e8f0', marginRight: '4px' }}>
                                                        {filteredSubDomains.map((sub, sIdx) => {
                                                            const isSubActive = selectedSkill?.name === sub.name;
                                                            return (
                                                                <div key={`other-sub-${sIdx}`} className="mb-2">
                                                                    <div className="d-flex align-items-center justify-content-between p-1 bg-light rounded">
                                                                        <div
                                                                            className={`d-flex align-items-center cursor-pointer font-weight-bold ${isSubActive ? 'text-info' : 'text-secondary'}`}
                                                                            style={{ fontSize: '0.76rem', gap: '0.3rem' }}
                                                                            onClick={() => handleFilterItem('component', sub)}
                                                                        >
                                                                            <i className="fas fa-folder-open text-info"></i>
                                                                            <span>{sub.name}</span>
                                                                        </div>
                                                                        <span className="font-weight-bold" style={{ fontSize: '0.72rem', color: getColorForPct(sub.masteryPct) }}>
                                                                            {Number(sub.userMarks).toFixed(1)} / {sub.maxMarks} ({sub.masteryPct}%)
                                                                        </span>
                                                                    </div>

                                                                    <div className="pr-2 pt-1" style={{ borderRight: '2px solid #cbd5e1', marginRight: '4px' }}>
                                                                        {sub.skills.map((skill, skIdx) => {
                                                                            const isSkillActive = selectedSkill?.name === skill.name;
                                                                            const status = getStatusInfo(skill);

                                                                            return (
                                                                                <div
                                                                                    key={`other-sk-${skIdx}`}
                                                                                    className={`skill-leaf-item cursor-pointer ${isSkillActive ? 'bg-indigo-50 font-weight-bold' : ''}`}
                                                                                    onClick={() => handleFilterItem('skill', skill)}
                                                                                    style={{ cursor: 'pointer' }}
                                                                                >
                                                                                    <div className="d-flex align-items-center" style={{ gap: '0.35rem' }}>
                                                                                        <i className={`fas ${status.icon}`} style={{ color: status.color, fontSize: '0.75rem' }}></i>
                                                                                        <span className="text-dark" style={{ fontSize: '0.75rem' }}>
                                                                                            {skill.name}
                                                                                        </span>
                                                                                    </div>

                                                                                    <div className="d-flex align-items-center" style={{ gap: '0.4rem' }}>
                                                                                        <span className="text-muted" style={{ fontSize: '0.7rem' }}>
                                                                                            {Number(skill.userMarks).toFixed(1)}/{skill.maxMarks}
                                                                                        </span>
                                                                                        <div className="skill-leaf-bar">
                                                                                            <div className="skill-leaf-bar-fill" style={{ width: `${skill.masteryPct}%`, background: status.color }} />
                                                                                        </div>
                                                                                        <span className="font-weight-bold" style={{ color: status.color, minWidth: '28px' }}>
                                                                                            {skill.masteryPct}%
                                                                                        </span>
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
